import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarCheckIcon, ChevronRightIcon, HeartIcon, LockIcon, MailIcon, SendIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { ButtonLink } from '../../components/ui/ButtonLink';
import { Tag } from '../../components/ui/Tag';
import { BookingTicket } from '../../components/chat/BookingTicket';
import { ProSheet } from '../../components/ProSheet';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { budgetLabel } from '../../utils/event';
import { daysUntil, formatDate } from '../../utils/format';
import { stagger, rise } from '../../utils/motion';
import type { MusicEvent } from '../../types/marketplace';
type Tab = 'applied' | 'invites' | 'matches' | 'booked';
export function Gigs() {
  const m = useMarketplace();
  const {
    events,
    me,
    applied,
    matches,
    offers,
    visibleInvites,
    hiddenInviteCount,
    getEvent
  } = m;
  const [tab, setTab] = useState<Tab>('applied');
  const [proOpen, setProOpen] = useState(false);
  const myMatches = matches.filter(x => x.performerId === me.id);
  const acceptedOffers = offers.filter(o => o.status === 'accepted' && myMatches.some(x => x.id === o.matchId));
  const bookedMatchIds = new Set(acceptedOffers.map(o => o.matchId));
  const pending = applied.map(id => getEvent(id)).filter((e): e is MusicEvent => Boolean(e) && !m.hasMatch(e!.id, me.id));
  const inviteEvents = visibleInvites.map(id => getEvent(id)).filter((e): e is MusicEvent => Boolean(e));
  const activeMatches = myMatches.filter(x => !bookedMatchIds.has(x.id));
  return <div className="mx-auto w-full max-w-3xl px-5 pb-10 pt-6 lg:px-8 lg:pt-8" data-id="element-1208" data-mp="3rfe5yku5wrl8">
      <h1 className="text-[28px] font-bold tracking-[-0.015em]" data-id="element-1209" data-mp="3mhushn5agiuc">Gigs</h1>
      <div className="no-scrollbar -mx-5 mt-4 overflow-x-auto px-5" data-id="element-1210" data-mp="1qg15m9jg7ech">
        <SegmentedControl<Tab> ariaLabel="Gig status" layoutId="gigs-tab" size="sm" className="min-w-max" value={tab} onChange={setTab} options={[{
        value: 'applied',
        label: 'Applied',
        count: pending.length
      }, {
        value: 'invites',
        label: 'Invites',
        count: inviteEvents.length
      }, {
        value: 'matches',
        label: 'Matches',
        count: activeMatches.length
      }, {
        value: 'booked',
        label: 'Booked',
        count: acceptedOffers.length
      }]} data-id="element-1211" data-mp="1vecf2j5tzmu6" />
      </div>

      <AnimatePresence mode="wait" data-id="element-1212" data-mp="1hd9s8470i8i7">
        <motion.div key={tab} variants={stagger} initial="hidden" animate="show" exit={{
        opacity: 0,
        transition: {
          duration: 0.12
        }
      }} className="mt-6" data-id="element-1213" data-mp="1lfwpikmgclms">
          {tab === 'applied' && (pending.length ? <ul className="divide-y divide-line" data-id="element-1214" data-mp="2wujz30v7wm2p">
                {pending.map(e => <GigRow key={e.id} event={e} trailing={<Tag tone="brand" data-id="element-1216" data-mp="l76p9y5z5e3m">Pending</Tag>} data-id="element-1215" data-mp="l881i5yq86mx" />)}
              </ul> : <EmptyState icon={<SendIcon className="h-7 w-7" data-id="element-1218" data-mp="h8qwter4jdit" />} title="No pending applications" body="Swipe right on events in Discover to apply." data-id="element-1217" data-mp="h768w7zbestx">
                <ButtonLink to="/p/discover" variant="primary" data-id="element-1219" data-mp="7hd0tgydsvuf">
                  Go to Discover
                </ButtonLink>
              </EmptyState>)}

          {tab === 'invites' && <div data-id="element-1220" data-mp="3tnialy11ac47">
              {inviteEvents.length > 0 && <ul className="divide-y divide-line" data-id="element-1221" data-mp="3tq4f1osmobi7">
                  {inviteEvents.map(e => <GigRow key={e.id} event={e} note={`${e.organiser} wants you on the line-up`} trailing={<div className="flex gap-2" data-id="element-1223" data-mp="1ivqk08i6wn44">
                          <Button variant="secondary" size="sm" onClick={() => m.declineInvite(e.id)} data-id="element-1224" data-mp="1ix1n063t9qu6">
                            Decline
                          </Button>
                          <Button variant="accent" size="sm" onClick={() => m.acceptInvite(e.id)} data-id="element-1225" data-mp="kk5t8nfrzg7s">
                            Accept
                          </Button>
                        </div>} data-id="element-1222" data-mp="3kn3c1wz270ir" />)}
                </ul>}
              {hiddenInviteCount > 0 && <motion.button variants={rise} type="button" onClick={() => setProOpen(true)} className="mt-4 flex w-full items-center gap-4 rounded-md bg-tint p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1226" data-mp="3syeetbjvr6qg">
                  <span className="flex -space-x-3" aria-hidden="true" data-id="element-1227" data-mp="3a14vrvigvv9q">
                    {events.filter(e => e.likesYou).slice(0, 3).map(e => <img key={e.id} src={e.photos[0]} alt="" className="h-11 w-11 rounded-full border-2 border-tint object-cover blur-[6px]" data-id="element-1228" data-mp="2vdcz540aujez" />)}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-1229" data-mp="35cly6ba0uo87">
                    <span className="block font-bold text-ink" data-id="element-1230" data-mp="35a9798mad3j0">
                      {hiddenInviteCount} more organiser{hiddenInviteCount > 1 ? 's' : ''} liked you
                    </span>
                    <span className="block text-sm text-ink-2" data-id="element-1231" data-mp="3eaxlo4fld5wf">See who with Encorza Pro and match instantly.</span>
                  </span>
                  <LockIcon className="h-5 w-5 shrink-0 text-brand-strong" data-id="element-1232" data-mp="3o3d3n1qw1uhb" />
                </motion.button>}
              {inviteEvents.length === 0 && hiddenInviteCount === 0 && <EmptyState icon={<MailIcon className="h-7 w-7" data-id="element-1234" data-mp="xcgr5jtqqfx" />} title="No invites right now" body="Organisers who like your profile will appear here." data-id="element-1233" data-mp="xugic5p3msl" />}
            </div>}

          {tab === 'matches' && (activeMatches.length ? <ul className="divide-y divide-line" data-id="element-1235" data-mp="3xw1hcrmsrj6">
                {activeMatches.map(x => {
            const e = getEvent(x.eventId);
            if (!e) return null;
            const offer = offers.find(o => o.matchId === x.id && o.status === 'pending');
            return <GigRow key={x.id} event={e} to={`/p/messages/${x.id}`} note={offer ? 'Offer waiting for you' : 'Matched · chat open'} trailing={offer ? <Tag tone="accent" data-id="element-1237" data-mp="wppfqyo4r2xq">Offer</Tag> : <ChevronRightIcon className="h-5 w-5 text-ink-3" data-id="element-1238" data-mp="wmjr1kjey32j" />} data-id="element-1236" data-mp="mqgqgj47mchp" />;
          })}
              </ul> : <EmptyState icon={<HeartIcon className="h-7 w-7" data-id="element-1240" data-mp="2yhlpq3ung4qe" />} title="No matches yet" body="When an organiser says yes to your application, you’ll match here." data-id="element-1239" data-mp="2yh2grcmz8qoi" />)}

          {tab === 'booked' && (acceptedOffers.length ? <div className="grid gap-6 sm:grid-cols-2" data-id="element-1241" data-mp="1ft9fuzcij5hm">
                {acceptedOffers.map(o => {
            const match = matches.find(x => x.id === o.matchId);
            const e = match ? getEvent(match.eventId) : undefined;
            return <motion.div key={o.id} variants={rise} data-id="element-1242" data-mp="16qi1ors95vma">
                      {e && <p className="mb-2 text-sm font-bold text-ink-2" data-id="element-1243" data-mp="23ghgv52wrsdu">
                          {daysUntil(e.date) >= 0 ? `In ${daysUntil(e.date)} days` : 'Played'}
                        </p>}
                      <BookingTicket offer={o} data-id="element-1244" data-mp="1pj2qny4tzgkt" />
                      {e && daysUntil(e.date) < 0 && <Button variant="outline" size="sm" className="mt-3" onClick={() => toast.success('Thanks for the review!')} data-id="element-1245" data-mp="j2h062thrfgd">
                          Review {e.organiser}
                        </Button>}
                    </motion.div>;
          })}
              </div> : <EmptyState icon={<CalendarCheckIcon className="h-7 w-7" data-id="element-1247" data-mp="2j6b0f3vpkerd" />} title="Nothing booked yet" body="Accept an offer in chat and your gig will land here." data-id="element-1246" data-mp="2j6kub2jvj62c" />)}
        </motion.div>
      </AnimatePresence>
      <ProSheet open={proOpen} onClose={() => setProOpen(false)} reason="See every organiser who liked you." data-id="element-1248" data-mp="298wmbax9c228" />
    </div>;
}
interface GigRowProps {
  event: MusicEvent;
  note?: string;
  trailing?: React.ReactNode;
  to?: string;
}
function GigRow({
  event,
  note,
  trailing,
  to
}: GigRowProps) {
  const body = <>
      <img src={event.photos[0]} alt="" className="h-16 w-16 shrink-0 rounded-sm object-cover" data-id="element-1249" data-mp="2s5obg0zhdbe7" />
      <div className="min-w-0 flex-1" data-id="element-1250" data-mp="3fv8zgyahqyuc">
        <p className="truncate font-bold" data-id="element-1251" data-mp="3fu7kwju9nu7v">{event.name}</p>
        <p className="truncate text-sm text-ink-2" data-id="element-1252" data-mp="3klz429b3exnz">
          {formatDate(event.date)} · {event.city.split(',')[0]} · {budgetLabel(event)}
        </p>
        {note && <p className="truncate text-sm font-semibold text-brand-strong" data-id="element-1253" data-mp="195fefpfknm1g">{note}</p>}
      </div>
    </>;
  return <motion.li variants={rise} className="flex flex-wrap items-center gap-3 py-3 sm:flex-nowrap" data-id="element-1254" data-mp="145sz9ag6nvq0">
      {to ? <Link to={to} className="flex min-w-0 flex-1 items-center gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1255" data-mp="1dxqdkq2hn3sy">
          {body}
        </Link> : <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-1256" data-mp="l91a4kdymme1">{body}</div>}
      <div className="ml-auto shrink-0" data-id="element-1257" data-mp="lb5ppzl819u5">{trailing}</div>
    </motion.li>;
}